'use client';
import { Swiper, SwiperSlide } from "swiper/react";
import { Mousewheel } from "swiper/modules";
import { useEffect, useRef, useState, useCallback } from "react";
import type { Swiper as SwiperType } from "swiper/types";
import CompactButton from "@/components/ui/CompactButton/CompactButton";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import useAspectRatio from "@/hooks/useAspectRatio";
import { useLenis } from "@/contexts/LenisContext";
import React from "react";
import Headline from "@/components/ui/Headline/Headline";
import { LanguageType, LanguageDirection, TeamMemberSectionContent } from "@/lib/types";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import gsap from 'gsap';
import { useGSAP } from "@gsap/react";
import TeamShowcaseCard from '@/components/client/TeamShowcaseCard/TeamShowcaseCard';

import { debug } from '@/lib/debug';
import { useIsLg, useIsTb } from '@/contexts/BreakpointContext';
gsap.registerPlugin(ScrollTrigger);

interface TeamSectionProps {
  language?: LanguageType
  headlineText: string
  subheadingText: string
  paragraphText: string
  onInitialPosition?: (position: number) => void
  teamMemberSectionPageData?: TeamMemberSectionContent
  shownPercentage?: number
  animateHeadline?: boolean
  isLandingPage?: boolean
  id?: string
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
};

interface ImageCard {
  id: number;
  imageUrl: string;
  imageAltText: string;
  title: string;
  description: string;
}

// const imageCards: ImageCard[] = [
//   {
//     id: 1,
//     // imageUrl: "https://picsum.photos/693/808?random=1",
//     imageUrl: "./hero-image.avif",
//     title: "Card 1",
//     description: "Description for card 1",
//   },
//   {
//     id: 2,
//     // imageUrl: "https://picsum.photos/693/808?random=2",
//     imageUrl: "./hero-image.avif",
//     title: "Card 2",
//     description: "Description for card 2",
//   },
//   {
//     id: 3,
//     // imageUrl: "https://picsum.photos/693/808?random=3",
//     imageUrl: "./hero-image.avif",
//     title: "Card 3",
//     description: "Description for card 3",
//   },
//   {
//     id: 4,
//     // imageUrl: "https://picsum.photos/693/808?random=4",
//     imageUrl: "./hero-image.avif",
//     title: "Card 4",
//     description: "Description for card 3",
//   },
//   {
//     id: 5,
//     // imageUrl: "https://picsum.photos/693/808?random=5",
//     imageUrl: "./hero-image.avif",
//     title: "Card 5",
//     description: "Description for card 3",
//   },
//   {
//     id: 6,
//     // imageUrl: "https://picsum.photos/693/808?random=6",
//     imageUrl: "./hero-image.avif",
//     title: "Card 6",
//     description: "Description for card 3",
//   },

// ];

const TeamSection: React.FC<TeamSectionProps> = ({
  language = "english",
  headlineText,
  subheadingText,
  paragraphText,
  onInitialPosition,
  teamMemberSectionPageData,
  shownPercentage = 0.2,
  animateHeadline = true,
  isLandingPage = false,
  id,
}) => {
  const aspectRatio = useAspectRatio();
  const isLg = useIsLg();
  const isTb = useIsTb();
  const swiperRef = useRef<SwiperType | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const { lenis } = useLenis();
  const [isContainerReady, setIsContainerReady] = useState(false);
  const [cardWidth, setCardWidth] = useState('458px');
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const imageCards: ImageCard[] = [...(teamMemberSectionPageData?.teamMembers ?? [])].sort((a, b) => a.displayOrder - b.displayOrder).map((teamMember: any, index: number) => ({
    id: index,
    imageUrl: teamMember.teamMemberImage.url,
    imageAltText: teamMember.teamMemberImageAltText,
    title: teamMember.teamMemberName,
    description: teamMember.teamMemberPosition
  })) ?? [];

  // debug.log('team section rendering');

  // Debounced ScrollTrigger refresh
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncedScrollTriggerRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    refreshTimeoutRef.current = setTimeout(() => {
      ScrollTrigger.refresh();
      // parallax is messed up by the scroll trigger refresh for some reason??
      // empirically, i noticed that if i scroll a bit, the parallax works again
      // so this is a workaround to fix the parallax
      // TODO: fix this properly
      if (lenis) {
        lenis.scrollTo(lenis.scroll + 1, { immediate: true });
      }
    }, 300);
  }, [lenis]);

  // Calculate card width based on container size and breakpoint
  const calculateCardWidth = useCallback((): string => {
    const containerWidth = containerRef.current?.offsetWidth ?? 1605;
    if (isLg) {
      return `${containerWidth / (3 + shownPercentage)}px`;
    } else if (isTb) {
      return `${containerWidth / (2 + shownPercentage)}px`;
    } else {
      return `${containerWidth / (1 + shownPercentage)}px`;
    }
  }, [isLg, isTb, shownPercentage]);

  // Initialize container and calculate widths
  useEffect(() => {
    if (containerRef.current && !isContainerReady) {
      setIsContainerReady(true);

      // Calculate initial width
      const initialWidth = calculateCardWidth();
      setCardWidth(initialWidth);
    }
  }, [calculateCardWidth, isContainerReady]);

  // Update width when media queries change
  useEffect(() => {
    if (isContainerReady) {
      const newWidth = calculateCardWidth();
      setCardWidth(newWidth);
      debouncedScrollTriggerRefresh();
    }
  }, [isLg, isTb, calculateCardWidth, isContainerReady, debouncedScrollTriggerRefresh]);

  // Recalculate card width on window resize (width changes only)
  useEffect(() => {
    if (!isContainerReady) return;

    let lastWidth = window.innerWidth;
    let resizeTimeout: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      // Skip height-only changes (mobile browser chrome appearing/disappearing)
      const currentWidth = window.innerWidth;
      if (currentWidth === lastWidth) return;
      lastWidth = currentWidth;

      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      resizeTimeout = setTimeout(() => {
        const newWidth = calculateCardWidth();
        setCardWidth(newWidth);
        debouncedScrollTriggerRefresh();
      }, 150);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
    };
  }, [isContainerReady, calculateCardWidth, debouncedScrollTriggerRefresh]);


  const handleNext = () => {
    if (swiperRef.current) {
      // ScrollTrigger.refresh();
      swiperRef.current.slideTo(swiperRef.current.activeIndex + 1, 300, true);
    }
  };

  const handlePrev = () => {
    if (swiperRef.current) {
      // ScrollTrigger.refresh();
      const prevIndex = swiperRef.current.activeIndex - 1;
      swiperRef.current.slideTo(prevIndex, 300, true);
    }
  };
  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.snapGrid = [...swiperRef.current.slidesGrid];

      // Listen for Swiper's init and update events
      // swiperRef.current.on('init', () => {
      //   ScrollTrigger.refresh();
      // });

      // swiperRef.current.on('resize', () => {
      //   ScrollTrigger.refresh();
      // });

      // swiperRef.current.on('update', () => {
      //   ScrollTrigger.refresh();
      // });

      // swiperRef.current.on('snapGridLengthChange', () => {
      //   ScrollTrigger.refresh();
      // });
    }

    return () => {
      if (swiperRef.current) {
        // Clean up event listeners
        // swiperRef.current.off('init');
        // swiperRef.current.off('snapGridLengthChange');
        // swiperRef.current.off('update');
        // ScrollTrigger.refresh();
      }
    };
  }, []);

  // Refresh when screen size changes affect card width
  // useEffect(() => {
  //   ScrollTrigger.refresh();
  // }, [isLg, isTb]);

  useEffect(() => {
    // ScrollTrigger.refresh();

        // ScrollTrigger.refresh();
    if (sectionRef.current && onInitialPosition && isContainerReady) {
      const rect = sectionRef.current.getBoundingClientRect();
      const topY = window.scrollY + rect.top;
      if (!isLg) {
        ScrollTrigger.refresh();
      }
      onInitialPosition(topY);
    }
  }, [aspectRatio, cardWidth, isContainerReady, onInitialPosition, isLg]);

  useGSAP(() => {
    // Select all team cards
    const container = containerRef.current;
    if (container && isLg && isContainerReady) {

      const cards: HTMLElement[] = Array.from(document.querySelectorAll('.team-member-card'));

      if (cards.length === 0) return;

      gsap.fromTo(cards, {
        yPercent: -10
      }, {
        yPercent: 10, // Subtle upward movement
        ease: "none",
        force3D: true,
        scrollTrigger: {
          trigger: container,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
          // markers: true,
          id: "team-section-scroll-trigger"
        },
        onEnter: () => {
          cards.forEach(card => {
            card.style.willChange = "transform";
          });
        },
        onLeave: () => {
          cards.forEach(card => {
            card.style.willChange = "auto";
          });
        },
        onEnterBack: () => {
          cards.forEach(card => {
            card.style.willChange = "transform";
          });
        },
        onLeaveBack: () => {
          cards.forEach(card => {
            card.style.willChange = "auto";
          });
        }
      });
    }
  }, { dependencies: [isTb, isLg, isContainerReady, cardWidth, aspectRatio], revertOnUpdate: true }); // Scope the animations to the container

  return (
    // section has custom padding!
    <section
      ref={sectionRef}
      id={id}
      style={{ overscrollBehaviorX: 'none' }}
      className={`
      w-full flex flex-col items-center justify-center
      gap-[2rem] lg:gap-[6rem]
      padding-main-x-mobile
      padding-main-x-desktop
      ${isLandingPage ? '' : 'pt-[1rem] padding-main-t-desktop'}
      overflow-hidden
      `}>
      <div
        className="
        w-full container-large"
      >

        <div
          style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
          className="
            w-full flex flex-col lg:flex-row lg:flex-wrap
            gap-y-[2rem] lg:gap-y-[3.5rem] lg:gap-x-[2rem]
            px-[0.5rem] lg:px-0
        ">
          <Headline
            type="short"
            alignment={language === "english" ? 'left' : 'right'}
            heading={headlineText}
            paragraph={paragraphText}
            subheading={subheadingText}
            language={language}
            shouldAnimate={animateHeadline}
          />

          {isLg && <div style={{ direction: "ltr" }} className="flex-grow min-w-[11.6875rem] w-[11.6875rem] max-w-full self-end">
            <div className={`flex flex-grow flex-row ${language === "english" ? 'justify-end' : 'justify-start'} gap-[0.75rem] lg:gap-[1rem]`}>
              <CompactButton
                size={isLg ? 64 : 44}
                radius={9999}
                className="left-0 bottom-0"
                onClick={language === "english" ? handlePrev : handleNext}
                disabled={language === "english" ? isBeginning : isEnd}
              >
                <CaretLeft />
              </CompactButton>

              <CompactButton
                size={isLg ? 64 : 44}
                radius={9999}
                className="right-0 bottom-0"
                onClick={language === "english" ? handleNext : handlePrev}
                disabled={language === "english" ? isEnd : isBeginning}
              >
                <CaretRight />
              </CompactButton>
            </div>
          </div>}
        </div>

      </div>

      <div
        ref={containerRef}
        style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
        className="w-full container-large overflow-visible">
        {/* should this clip on the left side and let it overflow on the right side ?? */}
        {isContainerReady && (
          <Swiper
            modules={[Mousewheel]}
            spaceBetween={isLg ? 16 : 12}
            style={{ overflow: 'visible' }}
            slidesPerView="auto"
            onBeforeInit={(swiper) => {
              swiperRef.current = swiper;
            }}
            onAfterInit={(swiper) => {
              setIsBeginning(swiper.isBeginning);
              setIsEnd(swiper.isEnd);
            }}
            onSlideChange={(swiper) => {
              setIsBeginning(swiper.isBeginning);
              setIsEnd(swiper.isEnd);
            }}
            updateOnWindowResize={false}
            observer={true}
            observeParents={true}
            resizeObserver={false}
            centeredSlidesBounds={isLg ? false : false}
            centeredSlides={isLg ? false : false}
            // mousewheel={{
            //   forceToAxis: true,
            //   sensitivity: 0.5,
            //   releaseOnEdges: true,
            // }}
          >

          {imageCards.map((card) => (
            <SwiperSlide key={card.id} style={{ width: 'auto', overflow: 'visible' }} className="cursor-grab">
              <TeamShowcaseCard
                imageUrl={card.imageUrl}
                imageAlt={card.imageAltText}
                title={card.title}
                description={card.description}
                cardWidth={cardWidth}
                isLandingPage={isLandingPage}
              />
            </SwiperSlide>
          ))}
          {/*
          <SwiperSlide style={{ width: 'auto' }}>
            <div className="invisible border w-[0.5rem] lg:w-[2.5rem]"></div>
          </SwiperSlide>
          */}

        </Swiper >
        )}
      </div >
    </section>
  );
}


export default TeamSection;
